import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import "./CorporatePicker.css";

const OPTION_LIMIT = 40;
const identity = (value) => `${typeof value}:${String(value)}`;
const normalize = (text) => String(text ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase();

/** A search-first picker that keeps even large corporate catalogues bounded in the DOM. */
export default function CorporatePicker({ label, placeholder = "Select an option", options = [], value, onChange, multiple = false, disabled = false, compact = false }) {
  const id = useId();
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const dialogRef = useRef(null);
  const inputRef = useRef(null);
  const closingRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const indexedOptions = useMemo(() => options.map((option) => ({ ...option, key: identity(option.value), search: normalize(`${option.label} ${option.description || ""} ${option.searchText || ""}`) })), [options]);
  const values = multiple ? (Array.isArray(value) ? value : []) : (value === "" || value === null || value === undefined ? [] : [value]);
  const selectedKeys = new Set(values.map(identity));
  const selectedOptions = indexedOptions.filter((option) => selectedKeys.has(option.key));
  const matches = useMemo(() => {
    const terms = normalize(query).trim().split(/\s+/).filter(Boolean);
    return indexedOptions.filter((option) => terms.every((term) => option.search.includes(term)));
  }, [indexedOptions, query]);
  const visibleOptions = matches.slice(0, OPTION_LIMIT);
  const safeIndex = Math.min(activeIndex, Math.max(visibleOptions.length - 1, 0));
  const activeId = visibleOptions.length ? `${id}-option-${safeIndex}` : undefined;
  const selectedLabel = selectedOptions.map((option) => option.label).slice(0, 2).join(", ");

  const close = (returnFocus = false) => {
    closingRef.current = true;
    setOpen(false);
    setQuery("");
    setActiveIndex(0);
    if (returnFocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return undefined;
    closingRef.current = false;
    inputRef.current?.focus();
    const dismiss = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        setQuery("");
        setActiveIndex(0);
      }
    };
    document.addEventListener("pointerdown", dismiss);
    const keepFocus = (event) => {
      if (!closingRef.current && !dialogRef.current?.contains(event.target)) inputRef.current?.focus();
    };
    document.addEventListener("focusin", keepFocus);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("focusin", keepFocus);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (open && activeId) document.getElementById(activeId)?.scrollIntoView?.({ block: "nearest" });
  }, [open, activeId]);

  const choose = (option) => {
    if (!option || disabled) return;
    if (multiple) {
      onChange(selectedKeys.has(option.key) ? values.filter((entry) => identity(entry) !== option.key) : [...values, option.value]);
      inputRef.current?.focus();
    } else {
      onChange(option.value);
      close(true);
    }
  };

  const handleKeys = (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((index) => Math.max(0, Math.min(visibleOptions.length - 1, index + direction)));
    } else if (event.key === "Enter") {
      event.preventDefault();
      choose(visibleOptions[safeIndex]);
    }
  };

  return (
    <div className={`cp-picker${compact ? " cp-picker--compact" : ""}${open ? " cp-picker--open" : ""}`} ref={rootRef} onKeyDown={(event) => {
      if (open && event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        close(true);
      }
      if (open && event.key === "Tab") {
        const controls = [...dialogRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled), [tabindex="0"]')];
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        event.stopPropagation();
      }
    }} onBlur={(event) => {
      if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) close();
    }}>
      {!compact && <span className="cp-picker-label" id={`${id}-label`}>{label}</span>}
      <button ref={triggerRef} type="button" className="cp-picker-trigger" disabled={disabled} aria-label={label} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? `${id}-dialog` : undefined} onClick={() => open ? close() : setOpen(true)} onKeyDown={(event) => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") { event.preventDefault(); setOpen(true); }
      }}>
        <span className={`cp-picker-value${!values.length ? " cp-picker-placeholder" : ""}`}>
          {compact ? label : (selectedLabel || placeholder)}
        </span>
        {multiple && values.length > 0 && <span className="cp-picker-count">{values.length}</span>}
        <svg className="cp-picker-chevron" viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6 4 4 4-4" /></svg>
      </button>
      {open && !disabled && <div className="cp-picker-backdrop" onClick={(event) => { if (event.target === event.currentTarget) close(true); }}>
        <section className="cp-picker-popover" ref={dialogRef} id={`${id}-dialog`} role="dialog" aria-modal="true" aria-labelledby={`${id}-title`}>
        <header className="cp-picker-head"><h3 id={`${id}-title`}>{label}</h3><button type="button" aria-label={`Close ${label.toLocaleLowerCase()} selection`} onClick={() => close(true)}>×</button></header>
        <div className="cp-picker-search">
          <svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="8.5" cy="8.5" r="5.5" /><path d="m13 13 4 4" /></svg>
          <input ref={inputRef} type="search" role="combobox" aria-label={`Search ${label.toLocaleLowerCase()}`} aria-autocomplete="list" aria-expanded="true" aria-controls={`${id}-list`} aria-activedescendant={activeId} autoComplete="off" placeholder="Search by name or code…" value={query} onChange={(event) => { setQuery(event.target.value); setActiveIndex(0); }} onKeyDown={handleKeys} />
        </div>
        {multiple && values.length > 0 && <div className="cp-picker-selected" aria-label="Selected options">
          {selectedOptions.slice(0, 3).map((option) => <button className="cp-picker-chip" type="button" key={option.key} title={option.label} aria-label={`Remove ${option.label}`} onClick={() => { onChange(values.filter((entry) => identity(entry) !== option.key)); inputRef.current?.focus(); }}><span>{option.label}</span><span aria-hidden="true">×</span></button>)}
          {values.length > 3 && <span className="cp-picker-more">+{values.length - 3} more</span>}
        </div>}
        <div id={`${id}-list`} className="cp-picker-options" role="listbox" aria-label={label} aria-multiselectable={multiple || undefined}>
          {visibleOptions.map((option, index) => <div key={option.key} id={`${id}-option-${index}`} role="option" aria-selected={selectedKeys.has(option.key)} className={`cp-picker-option${safeIndex === index ? " cp-picker-option--active" : ""}`} onMouseEnter={() => setActiveIndex(index)} onMouseDown={(event) => event.preventDefault()} onClick={() => choose(option)}>
            {multiple && <span className="cp-picker-check" aria-hidden="true">{selectedKeys.has(option.key) ? "✓" : ""}</span>}
            <span className="cp-picker-option-copy"><span>{option.label}</span>{option.description && <small>{option.description}</small>}</span>
            {!multiple && selectedKeys.has(option.key) && <span className="cp-picker-single-check" aria-hidden="true">✓</span>}
          </div>)}
        </div>
        {!matches.length && <p className="cp-picker-empty" role="status">No matches. Try another name or code.</p>}
        <div className="cp-picker-footer">
          <span role="status">{matches.length > OPTION_LIMIT ? `First ${OPTION_LIMIT} of ${matches.length.toLocaleString("en-GB")} · refine your search` : `${matches.length.toLocaleString("en-GB")} ${matches.length === 1 ? "option" : "options"}`}</span>
          {values.length > 0 && <button type="button" onClick={() => { onChange(multiple ? [] : ""); inputRef.current?.focus(); }}>Clear{multiple ? " all" : ""}</button>}
          <button type="button" className="cp-picker-done" onClick={() => close(true)}>{multiple ? "Done" : "Close"}</button>
        </div>
        </section>
      </div>}
    </div>
  );
}
